import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { WIRE_INSTRUCTIONS, WIRE_ROWS } from "@/lib/wireInstructions";

const API = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL || "" });

function fmt$(n) {
  return "$" + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Item prices read like a price sheet: whole dollars unless there are cents.
function fmtItem$(n) {
  const v = Number(n) || 0;
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 });
}

function quantityLabel(li) {
  const qty = Number(li.quantity) || 1;
  if (qty <= 1 || !li.unit_type) return "";
  const unit = li.unit_type.replace(/_/g, " ");
  return `${qty % 1 === 0 ? qty : qty.toFixed(1)} ${unit}${unit.endsWith("s") ? "" : "s"}`;
}

const CheckDot = ({ on }) => (
  <span style={{ ...s.checkDot, ...(on ? s.checkDotOn : {}) }} aria-hidden="true">
    {on && (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    )}
  </span>
);

const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#2E7D55" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/>
    </path>
  </svg>
);

export default function ClientApprovalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [co, setCo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentState, setPaymentState] = useState("idle");
  const [paymentType, setPaymentType] = useState(null);
  const [expired, setExpired] = useState(false);
  const [showWire, setShowWire] = useState(false);
  // Itemized change orders: which items the client is approving. Starts as
  // everything not already declined; saved to HQ before any payment.
  const [selected, setSelected] = useState(() => new Set());
  const [savingSelection, setSavingSelection] = useState(false);
  const [selectionError, setSelectionError] = useState("");


  useEffect(() => {
    if (!co?.itemized) return;
    setSelected(new Set((co.line_items || []).filter(li => li.client_decision !== "declined").map(li => li.id)));
  }, [co?.id, co?.itemized, co?.client_selection_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTotal = useMemo(
    () => (co?.line_items || []).reduce((sum, li) => sum + (selected.has(li.id) ? Number(li.price) || 0 : 0), 0),
    [co, selected]
  );

  useEffect(() => {
    API.get(`/api/public/change-orders/${token}`)
      .then(r => { setCo(r.data); setLoading(false); })
      .catch(err => {
        if (err?.response?.status === 410) setExpired(true);
        setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    const sid = searchParams.get("checkout_session_id");
    if (!sid || !token) return;
    setPaymentState("polling");
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const r = await API.get(`/api/public/change-orders/${token}/checkout-status/${sid}`);
        if (r.data.is_paid) {
          clearInterval(interval);
          setPaymentType(r.data.payment_type);
          setPaymentState("paid");
          const updated = await API.get(`/api/public/change-orders/${token}`);
          setCo(updated.data);
        } else if (attempts >= 15) {
          clearInterval(interval);
          setPaymentState("failed");
        }
      } catch {
        if (attempts >= 5) { clearInterval(interval); setPaymentState("failed"); }
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [token, searchParams]);

  // The approved total is recomputed by HQ from its own prices; the page only
  // says which items. Saved before payment so the link charges that total.
  const saveSelection = async () => {
    const r = await API.post(`/api/public/change-orders/${token}/selection`, {
      approved_item_ids: [...selected],
    });
    setCo(r.data);
    return r.data;
  };

  const handleConfirmForWire = async () => {
    setSavingSelection(true);
    setSelectionError("");
    try {
      await saveSelection();
    } catch (err) {
      setSelectionError(err?.response?.data?.detail || "We couldn't save your approval. Please try again.");
    } finally {
      setSavingSelection(false);
    }
  };

  const handlePay = async (isBalance = false) => {
    setPaying(true);
    try {
      if (co?.itemized && !isBalance) await saveSelection();
      const origin = window.location.origin;
      const endpoint = isBalance
        ? `/api/public/change-orders/${token}/pay-balance`
        : `/api/public/change-orders/${token}/pay`;
      const r = await API.post(endpoint, { origin_url: origin });
      window.location.href = r.data.url;
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to initiate payment. Please try again.");
      setPaying(false);
    }
  };

  const Shell = ({ children }) => (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logoWrap}>
          <img src="/trp-logo-new.png" alt="The Render Pros" style={s.logo} />
        </div>
        {children}
      </div>
      <Footer />
    </div>
  );

  if (loading) return (
    <Shell>
      <div style={s.centered}>
        <SpinnerIcon />
        <p style={{ ...s.muted, marginTop: 12 }}>Loading your proposal…</p>
      </div>
    </Shell>
  );

  if (expired) return (
    <Shell>
      <div style={s.centered}>
        <p style={s.title}>This link has expired</p>
        <p style={s.muted}>For your security, proposal links go inactive 7 days after they're sent.<br />Please contact your project manager for a new link.</p>
      </div>
    </Shell>
  );

  if (!co) return (
    <Shell>
      <div style={s.centered}>
        <p style={s.title}>Link not found</p>
        <p style={s.muted}>This proposal link is invalid.<br />Please contact your project manager.</p>
      </div>
    </Shell>
  );

  const isUpsell = co.request_type === "upsell";
  const depositPaid = co.deposit_paid;
  const balancePaid = co.balance_paid;
  const depositAmount = co.deposit_amount || co.final_client_price || co.total_amount || 0;
  const balanceAmount = co.balance_amount || 0;
  const total = co.final_client_price || co.total_amount || 0;
  const title = co.title || co.category || "Additional Scope";
  const project = co.project_name || "";
  const lineItems = co.line_items || [];
  const isItemized = !!co.itemized && !isUpsell;
  const awaitingAction = !(balancePaid || (depositPaid && !isUpsell));
  // Once money has been taken the selection is final — the page turns into a
  // record of what was approved.
  const selectionLocked = !awaitingAction;
  const toggleItem = (id) => {
    if (selectionLocked) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const dueNow = isItemized && !selectionLocked ? selectedTotal : depositAmount;
  const daysRemaining = co.days_remaining;
  const showExpiryPill = awaitingAction && daysRemaining !== null && daysRemaining !== undefined;
  const expiryLabel = daysRemaining === 0
    ? "Expires today"
    : daysRemaining === 1
      ? "1 day remaining"
      : `${daysRemaining} days remaining`;

  if (paymentState === "polling") return (
    <Shell>
      <div style={s.centered}>
        <SpinnerIcon />
        <p style={{ ...s.muted, marginTop: 12 }}>Confirming your payment…</p>
      </div>
    </Shell>
  );

  if (paymentState === "paid") {
    const wasDeposit = paymentType === "deposit" || !paymentType;
    return (
      <Shell>
        <div style={s.centered}>
          <CheckCircleIcon />
          <p style={{ ...s.title, marginTop: 16 }}>Payment received</p>
          <p style={s.body}>
            {wasDeposit
              ? isUpsell
                ? `Your deposit of ${fmt$(depositAmount)} has been received. Work will begin shortly. The remaining balance of ${fmt$(balanceAmount)} will be due upon delivery.`
                : `Your payment of ${fmt$(depositAmount)} has been received. Work will begin shortly.`
              : `Your final balance of ${fmt$(balanceAmount)} has been received. Thank you!`}
          </p>
          <p style={{ ...s.muted, marginTop: 12, fontSize: 12 }}>
            Your project manager will be in touch once production begins.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Logo */}
        <div style={s.logoWrap}>
          <img src="/trp-logo-new.png" alt="The Render Pros" style={s.logo} />
        </div>

        {/* Doc type badge */}
        <div style={s.badgeRow}>
          <span style={s.badge}>{isUpsell ? "Upsell Proposal" : "Change Order"}</span>
        </div>

        {/* Days remaining until link goes inactive */}
        {showExpiryPill && (
          <div style={s.badgeRow}>
            <span style={{ ...s.expiryPill, ...(daysRemaining <= 2 ? s.expiryPillUrgent : {}) }}>
              {expiryLabel}
            </span>
          </div>
        )}

        {/* Title */}
        <h1 style={s.title}>{title}</h1>
        {project && <div style={s.chip}><span style={s.chipInner}>{project}</span></div>}

        {/* Description */}
        {co.description && (
          <p style={{ ...s.body, marginTop: 18, textAlign: "left" }}>{co.description}</p>
        )}

        {/* Divider */}
        <div style={s.divider} />

        {/* Itemized change order — the client picks what goes ahead */}
        {isItemized && (
          <div style={s.section} data-testid="client-itemized-scope">
            <div style={s.sectionLabel}>Scope of Work</div>
            {!selectionLocked && (
              <p style={s.itemHint}>Approve everything, or only the items you'd like us to go ahead with.</p>
            )}
            <div>
              {lineItems.map(li => {
                const on = selectionLocked ? li.client_decision !== "declined" : selected.has(li.id);
                const qty = quantityLabel(li);
                const sub = [li.description, qty].filter(Boolean).join(" · ");
                return (
                  <div
                    key={li.id}
                    role="checkbox"
                    aria-checked={on}
                    tabIndex={selectionLocked ? -1 : 0}
                    onClick={() => toggleItem(li.id)}
                    onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggleItem(li.id); } }}
                    style={{ ...s.itemRow, cursor: selectionLocked ? "default" : "pointer" }}
                    data-testid={`client-item-${li.id}`}
                  >
                    <CheckDot on={on} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...s.itemName, ...(on ? {} : s.itemOff) }}>{li.item_name}</div>
                      {sub && <div style={s.itemSub}>{sub}</div>}
                    </div>
                    <div style={{ ...s.itemPrice, ...(on ? {} : s.itemPriceOff) }}>
                      {selectionLocked && !on ? "Not included" : fmtItem$(li.price)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={s.selTotalRow}>
              <div>
                <div style={s.selTotalLabel}>{selectionLocked ? "Approved total" : "Selected scope total"}</div>
                <div style={s.selCount}>
                  {(selectionLocked ? lineItems.filter(li => li.client_decision !== "declined").length : selected.size)} of {lineItems.length} items {selectionLocked ? "approved" : "selected"}
                </div>
              </div>
              <div style={s.selTotal} data-testid="client-selected-total">
                {fmtItem$(selectionLocked ? depositAmount : selectedTotal)}
              </div>
            </div>
            {!selectionLocked && (
              <div style={s.selButtons}>
                <button type="button" style={s.selButton} onClick={() => setSelected(new Set(lineItems.map(li => li.id)))} data-testid="client-select-all">
                  Select all
                </button>
                <button type="button" style={s.selButton} onClick={() => setSelected(new Set())} data-testid="client-clear-selection">
                  Clear selection
                </button>
              </div>
            )}
            {!selectionLocked && co.client_selection_at && (
              <p style={s.savedNote}>Your approval is saved. You can still change it until payment is made.</p>
            )}
          </div>
        )}

        {/* Line items */}
        {!isItemized && lineItems.length > 0 && (
          <div style={s.section}>
            <div style={s.sectionLabel}>Scope of Work</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={s.th}>Item</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Qty</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 450, fontSize: 14, color: "#000000" }}>{li.item_name}</div>
                      {li.description && <div style={{ fontSize: 12, color: "#8A8783", marginTop: 3 }}>{li.description}</div>}
                    </td>
                    <td style={{ ...s.td, textAlign: "right", color: "#302E2B", fontSize: 13 }}>
                      {li.quantity || 1}{li.unit_type ? ` ${li.unit_type.replace(/_/g, " ")}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Price block — an itemized order states its total in the list above */}
        {!isItemized && (
        <div style={s.priceCard}>
          <div style={s.priceInner}>
            <div>
              <div style={s.priceLabel}>
                {depositPaid && isUpsell
                  ? "Balance Due"
                  : isUpsell
                    ? "Deposit Due Today (50%)"
                    : "Total Investment"}
              </div>
              <div style={s.priceAmount}>
                {fmt$(depositPaid && isUpsell ? balanceAmount : depositAmount)}
              </div>
              {isUpsell && !depositPaid && (
                <div style={s.priceSub}>Total {fmt$(total)} · {fmt$(balanceAmount)} remaining due at delivery</div>
              )}
              {depositPaid && isUpsell && (
                <div style={s.priceSub}>Total {fmt$(total)} · Deposit of {fmt$(depositAmount)} already paid</div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Terms */}
        <p style={s.terms}>
          {depositPaid && !balancePaid && isUpsell
            ? "Your deposit has been received and production is underway. Clicking below will process your final balance."
            : isItemized
              ? "By completing payment you approve the items selected above. Production on those items begins once payment clears."
              : "By completing payment you confirm approval of the scope outlined above. Production begins once payment clears."}
        </p>

        {/* CTA */}
        {balancePaid ? (
          <div style={s.paidBanner}>
            <span style={{ marginRight: 8 }}>✓</span>Fully Paid — Thank you!
          </div>
        ) : depositPaid && !isUpsell ? (
          <div style={s.paidBanner}>
            <span style={{ marginRight: 8 }}>✓</span>Payment received — Production is underway
          </div>
        ) : (
          <>
            <button
              style={{ ...s.cta, opacity: paying || (isItemized && selected.size === 0) ? 0.55 : 1 }}
              disabled={paying || (isItemized && selected.size === 0)}
              onClick={() => handlePay(depositPaid && isUpsell)}
              data-testid="client-approve-pay"
            >
              {paying
                ? <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><SpinnerIcon /> Redirecting to Stripe…</span>
                : depositPaid && isUpsell ? "Pay Balance"
                : isItemized ? (selected.size === 0 ? "Select at least one item" : `Approve & Pay ${fmtItem$(selectedTotal)}`)
                : "Approve & Pay"}
            </button>

            {/* Wire transfer alternative */}
            {!showWire ? (
              <button style={s.wireToggle} onClick={() => setShowWire(true)}>
                Want to pay with wire?
              </button>
            ) : (
              <div style={s.wireWrap}>
                <p style={s.wireIntro}>
                  Send <strong style={{ color: "#000000" }}>{fmt$(depositPaid && isUpsell ? balanceAmount : dueNow)}</strong> to the account below.
                  Once your transfer clears we'll confirm receipt and begin production.
                </p>
                <div style={s.wireCard}>
                  {WIRE_ROWS.map(([label, value]) => (
                    <div key={label} style={s.wireRow}>
                      <span style={s.wireLabel}>{label}</span>
                      <span style={s.wireValue}>{value}</span>
                    </div>
                  ))}
                </div>
                <div style={s.wireNote}>
                  Please email <strong>{WIRE_INSTRUCTIONS.contact}</strong> with your transaction
                  reference so we can match your payment quickly.
                </div>
                {isItemized && (
                  <>
                    <button
                      type="button"
                      style={{ ...s.wireConfirm, opacity: savingSelection || selected.size === 0 ? 0.55 : 1 }}
                      disabled={savingSelection || selected.size === 0}
                      onClick={handleConfirmForWire}
                      data-testid="client-confirm-wire"
                    >
                      {savingSelection ? "Saving…" : `Approve ${selected.size} item${selected.size !== 1 ? "s" : ""} — I'll pay by wire`}
                    </button>
                    {selectionError && <p style={s.selectionError}>{selectionError}</p>}
                  </>
                )}
                <button style={s.wireBack} onClick={() => setShowWire(false)}>
                  ← Back to card payment
                </button>
              </div>
            )}
          </>
        )}

        {/* Cancelled notice */}
        {searchParams.get("cancelled") && (
          <p style={{ fontSize: 12, color: "#8A8783", textAlign: "center", marginTop: 14 }}>
            Payment was cancelled — you can try again above.
          </p>
        )}

        {/* Trust row */}
        <div style={s.trustRow}>
          <span style={s.trustItem}><LockIcon /> SSL encrypted</span>
          <span style={s.trustDot} />
          <span style={s.trustItem}>Powered by Stripe</span>
          <span style={s.trustDot} />
          <span style={s.trustItem}>The Render Pros</span>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <div style={s.footer}>
      <p style={{ margin: 0 }}>© {new Date().getFullYear()} The Render Pros · All rights reserved</p>
    </div>
  );
}

const s = {
  page: {
    position: "fixed",
    inset: 0,
    overflowY: "auto",
    background: "#F4F2EE",
    fontFamily: 'var(--font-sans)',
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "56px 16px 40px",
    zIndex: 9999,
  },
  card: {
    background: "#ffffff",
    borderRadius: 24,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 12px 40px rgba(0,0,0,0.08)",
    padding: "44px 48px 40px",
    maxWidth: 580,
    width: "100%",
  },
  logoWrap: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 32,
  },
  logo: {
    height: 36,
    objectFit: "contain",
  },
  badgeRow: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 22,
  },
  badge: {
    display: "inline-block",
    padding: "5px 14px",
    background: "#F4F2EE",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 450,
    letterSpacing: "1px",
    color: "#565350",
    textTransform: "uppercase",
  },
  expiryPill: {
    display: "inline-block",
    padding: "4px 12px",
    background: "#F1EDE5",
    border: "1px solid #DCD6C9",
    borderRadius: 100,
    fontSize: 11,
    fontWeight: 450,
    color: "#4A4235",
  },
  expiryPillUrgent: {
    background: "#FBEDE9",
    border: "1px solid #EDB7A6",
    color: "#8B331F",
  },
  title: {
    fontSize: 28,
    fontWeight: 300,
    letterSpacing: "-0.6px",
    color: "#000000",
    margin: "0 0 10px",
    textAlign: "center",
    lineHeight: 1.2,
  },
  chip: {
    display: "flex",
    justifyContent: "center",
    marginTop: 4,
    marginBottom: 0,
  },
  chipInner: {
    display: "inline-block",
    padding: "4px 14px",
    background: "#F4F2EE",
    borderRadius: 100,
    fontSize: 12,
    color: "#302E2B",
    fontWeight: 450,
  },
  body: {
    fontSize: 14,
    lineHeight: 1.7,
    color: "#302E2B",
    margin: 0,
    textAlign: "center",
  },
  muted: {
    fontSize: 14,
    color: "#8A8783",
    textAlign: "center",
    lineHeight: 1.6,
    margin: 0,
  },
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "16px 0 8px",
    textAlign: "center",
    gap: 4,
  },
  divider: {
    height: 1,
    background: "#F4F2EE",
    margin: "28px 0",
  },
  section: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 450,
    letterSpacing: "1.5px",
    color: "#8A8783",
    textTransform: "uppercase",
    marginBottom: 12,
  },
  th: {
    fontSize: 10,
    fontWeight: 450,
    letterSpacing: "1px",
    color: "#9E9B96",
    textTransform: "uppercase",
    padding: "0 0 8px",
    borderBottom: "1px solid #F4F2EE",
    textAlign: "left",
  },
  td: {
    padding: "12px 0",
    borderBottom: "1px solid #F4F2EE",
    verticalAlign: "top",
  },
  priceCard: {
    marginTop: 4,
    marginBottom: 4,
    background: "#000000",
    borderRadius: 18,
    padding: "28px 32px",
  },
  priceInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  priceLabel: {
    fontSize: 10,
    fontWeight: 450,
    letterSpacing: "1.5px",
    color: "rgba(255,255,255,0.5)",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  priceAmount: {
    fontSize: 46,
    fontWeight: 300,
    letterSpacing: "-1.5px",
    color: "#ffffff",
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1,
  },
  priceSub: {
    fontSize: 12,
    color: "rgba(255,255,255,0.4)",
    marginTop: 10,
    lineHeight: 1.5,
  },
  terms: {
    marginTop: 20,
    marginBottom: 0,
    fontSize: 12,
    color: "#8A8783",
    lineHeight: 1.65,
    textAlign: "center",
  },
  cta: {
    display: "block",
    width: "100%",
    marginTop: 20,
    padding: "17px 0",
    background: "#3D6355",
    color: "#fff",
    fontSize: 15,
    fontWeight: 450,
    letterSpacing: "-0.1px",
    border: "none",
    borderRadius: 14,
    cursor: "pointer",
    transition: "opacity 0.15s, transform 0.1s",
    boxShadow: "0 4px 16px rgba(0, 113, 227, 0.35)",
  },
  wireToggle: {
    display: "block",
    width: "100%",
    marginTop: 12,
    padding: "6px 0",
    background: "none",
    border: "none",
    fontSize: 13,
    fontWeight: 450,
    color: "#3D6355",
    cursor: "pointer",
    fontFamily: "inherit",
    textAlign: "center",
  },
  wireWrap: {
    marginTop: 16,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  wireIntro: {
    fontSize: 13,
    lineHeight: 1.6,
    color: "#302E2B",
    margin: 0,
    textAlign: "left",
  },
  wireCard: {
    background: "#F4F2EE",
    border: "1px solid #F1EFEA",
    borderRadius: 12,
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  wireRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 13,
    gap: 12,
  },
  wireLabel: { color: "#8A8783", flexShrink: 0 },
  wireValue: { fontWeight: 400, color: "#000000", fontFamily: "monospace", textAlign: "right" },
  wireNote: {
    padding: "12px 16px",
    background: "#ECF0EA",
    border: "1px solid #C3D0C8",
    borderRadius: 10,
    fontSize: 12.5,
    lineHeight: 1.55,
    color: "#3D6355",
    textAlign: "left",
  },
  wireBack: {
    background: "none",
    border: "none",
    fontSize: 13,
    color: "#3D6355",
    cursor: "pointer",
    padding: "2px 0",
    fontFamily: "inherit",
    alignSelf: "flex-start",
  },
  paidBanner: {
    marginTop: 20,
    padding: "15px 20px",
    background: "#E9F3EC",
    border: "1px solid #D2E7DA",
    borderRadius: 12,
    color: "#21593D",
    fontSize: 14,
    fontWeight: 450,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  itemHint: {
    fontSize: 13,
    color: "#565350",
    margin: "0 0 8px",
    lineHeight: 1.55,
  },
  itemRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "16px 0",
    borderBottom: "1px solid #F1EFEA",
    outline: "none",
  },
  checkDot: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    borderWidth: 1.5,
    borderStyle: "solid",
    borderColor: "#A8A49D",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 1,
    transition: "background 0.15s, border-color 0.15s",
  },
  checkDotOn: {
    background: "#000000",
    borderColor: "#000000",
  },
  itemName: {
    fontSize: 15,
    fontWeight: 500,
    color: "#000000",
    lineHeight: 1.35,
  },
  itemOff: {
    color: "#9E9B96",
  },
  itemSub: {
    fontSize: 13,
    color: "#8A8783",
    marginTop: 3,
    lineHeight: 1.5,
  },
  itemPrice: {
    fontSize: 15,
    fontWeight: 500,
    color: "#000000",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  itemPriceOff: {
    color: "#9E9B96",
    textDecoration: "line-through",
    fontWeight: 400,
  },
  selTotalRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: 18,
  },
  selTotalLabel: {
    fontSize: 15,
    fontWeight: 500,
    color: "#000000",
  },
  selCount: {
    fontSize: 12.5,
    color: "#8A8783",
    marginTop: 3,
  },
  selTotal: {
    fontSize: 26,
    fontWeight: 500,
    letterSpacing: "-0.5px",
    color: "#000000",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  selButtons: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginTop: 16,
  },
  selButton: {
    padding: "10px 0",
    background: "#ffffff",
    border: "1px solid #DCD8D1",
    borderRadius: 100,
    fontSize: 13,
    fontWeight: 450,
    color: "#302E2B",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  savedNote: {
    fontSize: 12,
    color: "#3D6355",
    margin: "12px 0 0",
    textAlign: "center",
  },
  wireConfirm: {
    display: "block",
    width: "100%",
    padding: "12px 0",
    background: "#ffffff",
    border: "1px solid #3D6355",
    borderRadius: 12,
    fontSize: 13.5,
    fontWeight: 450,
    color: "#3D6355",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  selectionError: {
    fontSize: 12,
    color: "#8B331F",
    margin: 0,
    textAlign: "center",
  },
  trustRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 24,
    paddingTop: 20,
    borderTop: "1px solid #F4F2EE",
    flexWrap: "wrap",
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    color: "#9E9B96",
    fontWeight: 450,
  },
  trustDot: {
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: "#D5D3CE",
    flexShrink: 0,
  },
  footer: {
    marginTop: 28,
    fontSize: 11,
    color: "#9E9B96",
    textAlign: "center",
  },
};
